import { ComponentProps, useState } from 'react';
import { css } from '@emotion/react';
import { type FallbackRender, ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { navigateToUrl } from '@frontend/auth';
import { theme } from '@frontend/theme';
import { Heading, Button, Text } from '@frontend/design-system';

export type BoundaryTier = 'app' | 'route' | 'feature';

const tierToLevel = {
  app: 'fatal',
  route: 'fatal',
  feature: 'error',
} as const;

type ErrorBoundaryProps = {
  /**
   * Tier is the way we track the blast radius of a triggered error boundary. Ideally, the blast radius is as small as possible.
   * Tier meanings:
   * - app: White screen of death, e.g. uncaught error in root component or error not caught by any lower boundary.
   *    Ideally never happens because a lower boundary should catch errors before they bubble up to this level, but if it does, it indicates a critical error that needs immediate attention.
   * - route: Error that causes an entire route to fail, e.g. error thrown during data fetching in a loader or an uncaught error in a route-level component.
   *    Not great because an entire route is broken, but not as bad as app-level because it’s contained to a specific route and doesn’t take down the whole app.
   * - feature: Error contained within a specific feature. Doesn't take down an entire route, impact is limited to specific feature or flow.
   *    Ideally should be contained within the feature and not affect other parts of the app, but still important to fix because it impacts a specific user flow.
   *
   * We can get more granular if needed in the future but 3 levels feels like a good starting point.
   *
   * This will drive some alerting rules in Sentry.
   */
  tier: BoundaryTier;
  /**
   * Feature is a free-form string that should be used to indicate what feature the error boundary is wrapping. This is helpful for debugging and alerting purposes so we can quickly identify what part of the app is impacted when an error is thrown.
   */
  feature: string;
  children?: React.ReactNode;
  error?: Error;
} & Omit<ComponentProps<typeof SentryErrorBoundary>, 'beforeCapture'>;

export function ErrorBoundary({ tier, feature, children, error, ...props }: ErrorBoundaryProps) {
  return (
    <SentryErrorBoundary
      fallback={GatekeeperError}
      {...props}
      beforeCapture={(scope) => {
        // we've had this tag on some error boundaries for awhile
        // seems useful for tracking all error boundary driven errors in Sentry
        scope.setTag('topic', 'errorBoundary');

        scope.setTag('boundary.tier', tier);
        scope.setTag('boundary.feature', feature);
        scope.setLevel(tierToLevel[tier]);
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
}

type Props = Parameters<FallbackRender>[0];

export function GatekeeperError({ error }: Pick<Props, 'error'>) {
  const [showingDetails, setShowingDetails] = useState(false);

  return (
    <div
      css={css`
        margin: ${theme.spacing(4)} auto auto;
        width: 400px;
        border-radius: ${theme.borderRadius.small};
        padding: ${theme.spacing(2)};
      `}
      data-testid='gatekeeper-error-boundary'
    >
      <Heading
        level={2}
        css={css`
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        Something went wrong.
      </Heading>
      <Text>Please contact support if this problem persists.</Text>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          gap: ${theme.spacing(2)};
          align-items: center;
        `}
      >
        <Button
          variant='primary'
          onClick={() => navigateToUrl('/', { reason: 'Error in error boundary, starting over', error })}
          id='error-start-over-button'
          css={{ flex: 1 }}
        >
          Try Again
        </Button>
        <Button
          size='small'
          variant='secondary'
          onClick={() => setShowingDetails(!showingDetails)}
          css={css`
            margin-left: auto;
            margin-top: ${theme.spacing(2)};
            margin-bottom: ${theme.spacing(2)};
          `}
          id='error-show-details-button'
        >
          Show Details
        </Button>
      </div>
      {showingDetails && (
        <pre
          css={css`
            overflow: auto;
            padding: ${theme.spacing(2)};
            background: ${theme.colors.neutral10};
            border-radius: ${theme.borderRadius.small};
            font-size: ${theme.fontSize(12)};
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 200px;
          `}
        >
          <code>{error instanceof Error ? error.message : 'Unknown error'}</code>
        </pre>
      )}
    </div>
  );
}
